import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SavedInstitutionsTab = ({ savedInstitutions, onRemove, onUpdateStatus }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date-added');

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'interested', label: 'Intéressé' },
    { value: 'applied', label: 'Candidature envoyée' },
    { value: 'accepted', label: 'Accepté' },
    { value: 'rejected', label: 'Refusé' },
    { value: 'waiting', label: 'En attente' }
  ];

  const sortOptions = [
    { value: 'date-added', label: 'Date d\'ajout' },
    { value: 'name', label: 'Nom alphabétique' },
    { value: 'deadline', label: 'Échéance proche' },
    { value: 'status', label: 'Statut' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      interested: 'bg-blue-100 text-blue-800',
      applied: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      waiting: 'bg-purple-100 text-purple-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      interested: 'Heart',
      applied: 'Send',
      accepted: 'CheckCircle',
      rejected: 'XCircle',
      waiting: 'Clock'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusLabel = (status) => {
    const labels = {
      interested: 'Intéressé',
      applied: 'Candidature envoyée',
      accepted: 'Accepté',
      rejected: 'Refusé',
      waiting: 'En attente'
    };
    return labels?.[status] || status;
  };

  const filteredInstitutions = savedInstitutions?.filter(inst => filterStatus === 'all' || inst?.applicationStatus === filterStatus)?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'deadline':
          return new Date(a.applicationDeadline) - new Date(b.applicationDeadline);
        case 'status':
          return a?.applicationStatus?.localeCompare(b?.applicationStatus);
        default:
          return new Date(b.dateAdded) - new Date(a.dateAdded);
      }
    });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Icon name="Bookmark" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Établissements Sauvegardés ({filteredInstitutions?.length})
            </h3>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filtrer par statut"
              className="w-full sm:w-48"
            />
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Trier par"
              className="w-full sm:w-48"
            />
          </div>
        </div>
      </div>
      {/* Institutions List */}
      {filteredInstitutions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="BookmarkX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Aucun établissement sauvegardé
          </h3>
          <p className="text-muted-foreground mb-6">
            Commencez à explorer les établissements et sauvegardez ceux qui vous intéressent.
          </p>
          <Link to="/post-bac-database">
            <Button variant="default" iconName="Search" iconPosition="left">
              Explorer les Établissements
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredInstitutions?.map((institution) => {
            const daysUntilDeadline = getDaysUntilDeadline(institution?.applicationDeadline);
            const isDeadlineSoon = daysUntilDeadline <= 7 && daysUntilDeadline > 0;
            const isDeadlinePassed = daysUntilDeadline < 0;

            return (
              <div key={institution?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-interactive transition-smooth">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                      <Image
                        src={institution?.logo}
                        alt={institution?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground mb-1 truncate">
                        {institution?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {institution?.program} • {institution?.city}
                      </p>
                      
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(institution?.applicationStatus)}`}>
                        <Icon name={getStatusIcon(institution?.applicationStatus)} size={12} />
                        <span>{getStatusLabel(institution?.applicationStatus)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => onRemove(institution?.id)}
                    className="p-1 hover:bg-muted rounded transition-smooth"
                    title="Supprimer de la liste"
                  >
                    <Icon name="X" size={16} className="text-muted-foreground" />
                  </button>
                </div>
                {/* Deadline Information */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Échéance de candidature:</span>
                    <span className={`font-medium ${
                      isDeadlinePassed ? 'text-destructive' : isDeadlineSoon ?'text-warning' : 'text-foreground'
                    }`}>
                      {formatDate(institution?.applicationDeadline)}
                    </span>
                  </div>
                  
                  {!isDeadlinePassed && (
                    <div className="mt-1">
                      <div className="flex items-center space-x-2 text-xs">
                        <Icon name="Clock" size={12} className="text-muted-foreground" />
                        <span className={`${isDeadlineSoon ? 'text-warning font-medium' : 'text-muted-foreground'}`}>
                          {daysUntilDeadline > 0 ? `${daysUntilDeadline} jours restants` : 'Échéance aujourd\'hui'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Progress Indicators */}
                {institution?.applicationStatus === 'applied' && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Progression:</span>
                      <span className="text-foreground font-medium">75%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                )}
                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <Link to={`/institution-details?id=${institution?.id}`} className="flex-1">
                    <Button variant="outline" size="sm" fullWidth>
                      Voir Détails
                    </Button>
                  </Link>
                  
                  {institution?.applicationStatus === 'interested' && (
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => onUpdateStatus(institution?.id, 'applied')}
                      iconName="Send"
                      iconPosition="left"
                    >
                      Postuler
                    </Button>
                  )}
                </div>
                {/* Additional Info */}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Ajouté le {formatDate(institution?.dateAdded)}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{institution?.competitiveness} concurrence</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedInstitutionsTab;